import React, { useState, useEffect } from 'react';
import { Form, Space, Button, Table, Pagination, Row, Col, Switch, Tooltip } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faEye, faClockRotateLeft, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { ProhibitedArea, FullHeightContainerLayout } from "components";
import CypressTestIds from "../../../cypress/CypressTestIds";

const { Column } = Table;

const RoleList = ({ permission, roleList, onEditRole, onViewRole, onViewRoleHistory, onActivateDeactivateRole, onDeleteRole, onAddRole, onPageSelect }) => {
    const [pagination, setPagination] = useState({ defaultPageSize: 100, pageSize: 100, page: 1 })
    const [paginatedRoleList, setPaginatedRoleList] = useState(roleList && roleList.slice(pagination.page - 1, pagination.pageSize));

    useEffect(() => {
        setPaginatedRoleList(roleList && roleList.slice((pagination.page - 1) * pagination.pageSize, (pagination.page * pagination.pageSize)));
    }, [roleList]);

    const onPageClick = (page, pageSize) => {
        let currentPagination = { ...pagination };
        let currentPaginatedRoleList = roleList && roleList.slice((page - 1) * pageSize, (page * pageSize));
        setPaginatedRoleList(currentPaginatedRoleList);
        currentPagination.page = page;
        currentPagination.pageSize = pageSize;
        setPagination(currentPagination);
    }

    if (!permission) {
        return <ProhibitedArea></ProhibitedArea>
    }
    return (
        <Row style={{ flexDirection: "column", flexGrow: 1, height: "100%" }}>
            <Col span={24} style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                <FullHeightContainerLayout
                    showHeader={false}
                    showFooter={true}
                    content={
                        <FullHeightContainerLayout
                            showHeader={false}
                            content={
                                <Table size="small"
                                    rowKey="roleId"
                                    dataSource={paginatedRoleList}
                                    scroll={{ y: "100vh" }}
                                    className="container-height-100"
                                    pagination={false}
                                    data-testid={CypressTestIds.ROLES_LIST_TABLE}>
                                    <Column
                                        width="6em"
                                        render={(value, role, index) => <>
                                        <Tooltip title ='Edit Role'>
                                            <FontAwesomeIcon
                                                icon={faPencil}
                                                className={role.type !== "SYSTEM" && permission.canEdit ? "action-cursor text-blue-500" : "text-gray-400 opacity-45"}
                                                onClick={() => role.type !== "SYSTEM" && permission.canEdit ? onEditRole(role.roleId) : null}
                                                data-testid={CypressTestIds.ROLES_LIST_EDIT_BUTTON}/> </Tooltip>
                                                <Tooltip title ='View Role'>
                                            <FontAwesomeIcon
                                                icon={faEye}
                                                className={permission.canView ? "action-cursor text-blue-500" : "text-gray-400 opacity-45"}
                                                onClick={() => permission.canView ? onViewRole(role.roleId) : null}
                                                data-testid={CypressTestIds.ROLES_LIST_VIEW_BUTTON}/> </Tooltip>
                                                <Tooltip title ='View Role History'>
                                            <FontAwesomeIcon
                                                icon={faClockRotateLeft}
                                                className={permission.canView ? "action-cursor text-blue-500" : "text-gray-400 opacity-45"}
                                                onClick={() => permission.canView ? onViewRoleHistory(role.roleId) : null}
                                                data-testid={CypressTestIds.ROLES_LIST_HISTORY_BUTTON}/> </Tooltip>
                                        </>}
                                    />
                                    <Column
                                         width="15%"
                                        dataIndex={"name"}
                                        title={"Role"}
                                        render={(value, role, index) => <>{value}</>}
                                    />
                                    <Column
                                        width="10%"
                                        dataIndex={"permissionsCount"}
                                        title={"Permissions"}
                                        render={(value, role, index) => <>{value || 0}</>}
                                    />
                                    <Column
                                        width="10%"
                                        dataIndex={"usersCount"}
                                        title={"Users"}
                                        render={(value, role, index) => <>{value || 0}</>}
                                    />
                                    <Column
                                        width="10%"
                                        dataIndex={"apiKeyCount"}
                                        title={"API Keys"}
                                        render={(value, role, index) => <>{value || 0}</>}
                                    />
                                    <Column
                                        width="10%"
                                        dataIndex="active"
                                        title={"Active"}
                                        ellipsis={{
                                            showTitle: false,
                                        }}
                                        render={(value, role, index) => {
                                            return <Switch
                                                checked={value}
                                                disabled={role.type === "SYSTEM" || !permission.canEdit}
                                                onClick={(checked, event) => onActivateDeactivateRole(role.roleId, role.version, checked)}
                                                data-testid={CypressTestIds.ROLES_LIST_ACTIVE_SWITCH}>
                                            </Switch>;
                                        }}
                                    />
                                    <Column
                                        width="5%"
                                        render={(value, role, index) => {
                                            return (
                                                 <Tooltip title ='Delete Role'>
                                                <FontAwesomeIcon
                                                    icon={faTrashCan}
                                                    className={role.type !== "SYSTEM" && permission.canDelete ? "action-cursor text-blue-500" : "text-gray-400 opacity-45"}
                                                    onClick={() => role.type !== "SYSTEM" && permission.canDelete ? onDeleteRole(role) : null}
                                                    data-testid={CypressTestIds.ROLES_LIST_DELETE_BUTTON}/> </Tooltip> 
                                            );
                                        }}
                                    />
                                </Table>
                            }
                            showFooter={true}
                            footer={
                                <Row justify="end" style={{ padding: "0.2rem 0rem" }}>
                                    <Col span={24} style={{ textAlign: "end" }}>
                                        <Pagination
                                            size="small"
                                            hideOnSinglePage={true}
                                            total={roleList.length}
                                            defaultPageSize={pagination.defaultPageSize}
                                            pageSize={pagination.pageSize}
                                            responsive={true}
                                            showSizeChanger={false}
                                            onChange={onPageClick} />
                                    </Col>
                                </Row>
                            }>
                        </FullHeightContainerLayout>
                    }
                    footer={
                        <Row className="table-footer-row">
                            <Col span={24} className="footer-right-column">
                                <Button
                                    disabled={!permission.canAdd}
                                    type="primary"
                                    onClick={onAddRole}
                                    data-testid={CypressTestIds.ROLES_LIST_ADD_ROLE_BUTTON}>
                                    <PlusOutlined />  Add Role
                                </Button>
                            </Col>
                        </Row>
                    }
                    footerStyle={{ paddingTop: "1rem" }}>

                </FullHeightContainerLayout>
            </Col>
        </Row>
    );
}

export default RoleList;